import {
  Box,
  FormControl,
  type FormControlProps,
  FormHelperText,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { isEqual } from "lodash-es";
import { type FC, type MouseEvent, useState } from "react";

type DayTypeOptions = "anyday" | "weekday" | "specific";

export const DaySelectorV2: FC<
  FormControlProps & {
    on_event_key: string[] | null;
    onUpdateEventKeys: (values: string[] | null) => void;
    helperText?: string;
    disable_specific?: boolean;
  }
> = ({ on_event_key, onUpdateEventKeys, helperText, disable_specific, ...props }) => {
  const [selectOption, setSelectOption] = useState<DayTypeOptions>(
    on_event_key === null
      ? "anyday"
      : isEqual(new Set(on_event_key), WEEKDAY_SERVER_VALUES_SET)
        ? "weekday"
        : "specific",
  );

  return (
    <FormControl error={Boolean(helperText)} {...props}>
      <TextField
        id="schedule-days-select"
        label="Send on"
        value={selectOption}
        select
        onChange={(event) => {
          const newSelectedOption = event.target.value;

          if (newSelectedOption === "weekday") onUpdateEventKeys(WEEKDAY_SERVER_VALUES);
          else if (newSelectedOption === "specific") onUpdateEventKeys([]);
          else onUpdateEventKeys(null);

          setSelectOption(event.target.value as DayTypeOptions);
        }}
      >
        <MenuItem value="anyday">On any day of the week</MenuItem>
        <MenuItem value="weekday">On any weekday</MenuItem>
        {(!disable_specific || selectOption === "specific") && (
          <MenuItem value="specific">Specific day(s) of the week</MenuItem>
        )}
      </TextField>

      {selectOption === "specific" && (
        <>
          <Box>
            <Typography mt={2}>
              Which day(s) of the week should the Communication be sent on?
            </Typography>
            <Box mt={1}>
              <DayButtonGroup
                disabled={props.disabled}
                on_event_key={on_event_key}
                onUpdateEventKeys={onUpdateEventKeys}
              />
            </Box>
          </Box>
          <FormHelperText sx={{ ml: 0 }}>{helperText}</FormHelperText>
        </>
      )}
    </FormControl>
  );
};

const DayButtonGroup: FC<{
  on_event_key: string[] | null;
  onUpdateEventKeys: (values: string[] | null) => void;
  disabled?: boolean;
}> = ({ on_event_key, onUpdateEventKeys, disabled }) => {
  const handleDays = (event: MouseEvent<HTMLElement>, newDays: string[]): void => {
    onUpdateEventKeys(newDays);
  };

  return (
    <ToggleButtonGroup
      disabled={disabled}
      value={on_event_key}
      onChange={handleDays}
      sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
    >
      {Object.entries(DAYS).map(([display, serverVal]) => (
        <ToggleButton
          data-analytics-id={`moment-form-schedule-day-${serverVal}`}
          key={serverVal}
          value={serverVal}
        >
          {display}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const DAYS = {
  Sun: "sunday",
  Mon: "monday",
  Tue: "tuesday",
  Wed: "wednesday",
  Thu: "thursday",
  Fri: "friday",
  Sat: "saturday",
};

const WEEKDAY_SERVER_VALUES = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const WEEKDAY_SERVER_VALUES_SET = new Set(WEEKDAY_SERVER_VALUES);
